import React, { Suspense, lazy, useCallback, useContext, useEffect, useRef, useState } from 'react';
import './App.scss';
import Header from './NewComponents/Header';

import { BrowserRouter , Navigate, Routes, Route, useLocation } from "react-router-dom";
import Footer from './NewComponents/Footer';
import Loader from './NewComponents/Loader';
import { getAccessToken } from './common/utils';
import { SocketContext } from './common/SocketService';
import { GlobalProvider } from './GlobalContext';
import ScrollToTop from './Components/ScrollToTop';
import FinancialDetails from './pages/FinancialDetails';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const GuruDashboard = lazy(() => import('./pages/GuruDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsCondition = lazy(() => import('./pages/TermsCondition'));
const Batches = lazy(() => import('./pages/Batches'));
const BatchDetails = lazy(() => import('./pages/BatchDetail'));
const Messages = lazy(() => import('./pages/Messages'));
const MessageDetails = lazy(() => import('./pages/MessageDetails'));
const Notification = lazy(() => import('./pages/Notification'));
const Favourites = lazy(() => import('./pages/Favourites'));
const ProfileSetting = lazy(() => import('./pages/ProfileSetting'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Explore = lazy(() => import('./pages/Explore'));
const CreateRequirement = lazy(() => import('./pages/CreateRequirement'));
const RequirementDetails = lazy(() => import('./pages/RequirementDetails'));
const ProfileOverview = lazy(() => import('./pages/ProfileOverview'));
const AddressPicker = lazy(() => import('./pages/AddressPicker'));
const VerifyOtp = lazy(() => import('./pages/VerifyOtp'));
const LinkedInSSOSuccess = lazy(() => import('./pages/LinkedInSSOSuccess'));
const GoogleSSOSuccess = lazy(() => import('./pages/GoogleSSOSuccess'));
const PaymentPage = lazy(() => import('./pages/Payment/PaymentPage'));

const getStoredAuthState = () => {
  const storedUserData = localStorage.getItem('userData');
  const parsedUserData = storedUserData ? JSON.parse(storedUserData) : null;

  return {
    isAuthenticated: Boolean(parsedUserData?.data?.data?.tokens?.access?.token),
    accountRole: parsedUserData?.data?.data?.account?.role,
  };
};

const ProtectedRoute = ({ element }) => {
  const { isAuthenticated } = getStoredAuthState();
  return isAuthenticated ? element : <Navigate to="/Login" replace />;
};

const DashboardRoute = () => {
  const { isAuthenticated, accountRole } = getStoredAuthState();

  if (!isAuthenticated) {
    return <Navigate to="/Login" replace />;
  }

  return accountRole === 'guru' ? <GuruDashboard /> : <Dashboard />;
};

function DeferredGlobalStyles() {
  const location = useLocation();
  const hasLoadedFullStyles = useRef(false);

  useEffect(() => {
    if (hasLoadedFullStyles.current) {
      return undefined;
    }

    const loadStyles = () => {
      if (hasLoadedFullStyles.current) {
        return;
      }

      hasLoadedFullStyles.current = true;
      import('./style.scss');
    };

    if (location.pathname !== '/') {
      loadStyles();
      return undefined;
    }

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(loadStyles, { timeout: 1500 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(loadStyles, 300);
    return () => window.clearTimeout(timeoutId);
  }, [location.pathname]);

  return null;
}

const App = () => {
  const socketContext = useContext(SocketContext);
  const socket = socketContext?.socket;
  const [message, setMessage] = useState("")
  const userData = JSON.parse(localStorage.getItem('userData'));

  const connectToSocket = useCallback(async () => {
    if (!socket) return;
    
    await getAccessToken().then(async token => {
      if (userData?.data?.data?.tokens?.access?.token) {
        socket.io.opts.extraHeaders = { "access-token": `Bearer ${userData?.data?.data?.tokens?.access?.token}` };
        socket.connect();
      }
    });
  }, [socket, userData]);

  useEffect(() => {
    if (!socket) return;
    
    if (socket.connected === false && userData) {
      connectToSocket()
    }

    const handleMessageReceive = (response) => {
      setMessage(response);
    };

    socket.on("MESSAGE_RECEIVE", handleMessageReceive);

    return () => {
      socket.off("MESSAGE_RECEIVE", handleMessageReceive);
    }

  }, [connectToSocket, socket, userData])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (message) {
        setMessage("")
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [message])

  return (
    <GlobalProvider>
      <BrowserRouter>
        <DeferredGlobalStyles />
        <div className="App">
          <Header />
          <ScrollToTop />
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route exact path="/" element={<LandingPage />} />
              <Route path="/Login" element={<Login />} />
              <Route path="/sso-success" element={<LinkedInSSOSuccess />} />
              <Route path="/google/sso-success" element={<GoogleSSOSuccess />} />
              <Route path="/Register" element={<Register />} />
              <Route path="/Forgotpassword" element={<ForgotPassword />} />
              <Route path="/dashboard" element={<DashboardRoute />} />
              <Route path="/Privacypolicy" element={<PrivacyPolicy />} />
              <Route path="/Termscondition" element={<TermsCondition />} />
              <Route path="/Batches" element={<Batches />} />
              <Route path="/batchdetails" element={<BatchDetails />} />
              <Route path="/messages" element={<ProtectedRoute element={<Messages />} />} />
              <Route path="/chat" element={<ProtectedRoute element={<MessageDetails />} />} />
              <Route path="/notification" element={<ProtectedRoute element={<Notification />} />} />
              <Route path="/favourites" element={<ProtectedRoute element={<Favourites />} />} />
              <Route path="/profilesetting" element={<ProtectedRoute element={<ProfileSetting />} />} />
              <Route path="/transactions" element={<ProtectedRoute element={<Transactions />} />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/createrequirement" element={<CreateRequirement />} />
              <Route path="/requirementDetails" element={<RequirementDetails />} /> 
              <Route path="/profileoverview" element={<ProfileOverview />} /> 
              <Route path="/addresspicker" element={<AddressPicker />} />
              <Route path='/verifyotp' element={<VerifyOtp />} />
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="/financial-details" element={<ProtectedRoute element={<FinancialDetails />} />} />
            </Routes>
          </Suspense>
        </div>
        <Footer />
      </BrowserRouter>
    </GlobalProvider>
  );
}

export default App;
